// src/annihilation/combat.js
// ============================================================
//  ANNIHILATION — the damage formula.
//  Source of truth: ANNIHILATION_GATING_DOCTRINE (PRIME
//  addendum) Parts 10–11, ANNIHILATION_SYSTEMS_SPEC (v2) Part 3.
//
//  THE MASTER FORMULA (hard architectural law — no percentages,
//  no hybrid mitigation, ever, without a caP'n re-decision):
//
//      damage_dealt = max(1, attacker.attack + skill.bonus - target.lyf)
//                     + decree
//
//  · Flat subtraction. Lyf 10 means "take 10 less." Readable on
//    a phone, predictable at every level, mental-math friendly.
//  · A hit never heals and never does zero to a live target.
//  · Status effect DoTs are UNMODIFIED by Lyf.
//
//  ⚑⚑ THE SMALL NUMBER LAW (Doctrine Part 10) ⚑⚑
//  This file has carried "no percentages" since it was written,
//  and the FORMULA obeyed it — the MODIFIERS never did. The law
//  now covers modifiers too, not just the master formula:
//
//  > Stat magnitude modifiers are FLAT, SINGLE-DIGIT, and stack
//  > slowly. Never percentages. A big number looks cooler and is
//  > almost always unnecessary.
//
//  It matters more now than it used to: with the allotment
//  transcribed from the cards, Lyf and Lyt naturally live in
//  0–10. Against that scale a percentage is either meaningless
//  or catastrophic. Four kinds of percentage are NOT stat
//  magnitudes and stay exactly as they are — chance (a proc),
//  pool fraction (reform at half Anima), spatial/temporal (dash
//  distance, move speed), and per-point scaling. The test is one
//  sentence: does this modify Anima, Chao, Lyf, Lyt, or damage
//  dealt? Yes → flat. No → leave it alone.
//
//  ⚑⚑ THE END OF CRIT (Doctrine Part 11) ⚑⚑
//  caP'n: "not enough skill too much luck... every game if
//  there's damage involved, make a crit build you win. So
//  annoying and boring and redundant. Not Illegal Empire at all."
//
//  BASE_CRIT_CHANCE and CRIT_MULTIPLIER are DELETED. Random crit
//  is a fake skill — the player did nothing to earn it, the dice
//  did, and because it was a STAT you could stack it until it
//  was the only build worth playing. Making it rarer only delays
//  that; any nonzero crit stat gets stacked eventually, because
//  that is what stats are for.
//
//  DECREE replaces it: flat bonus damage that lands because you
//  EARNED THE OPENING, never because a die agreed. Deterministic,
//  visible, buildable — and there is no crit-chance stat anywhere
//  in the game, so the crit-build meta cannot form. Not tuned
//  down. Structurally absent. Nodes make Decrees BIGGER or add
//  NEW TRIGGERS; none of them make a Decree more LIKELY, because
//  likelihood is not part of it.
//
//  (Pillar 6 says find the fake fail state and kill it. This is
//  the same instruction pointed the other way: find the fake
//  skill and kill it.)
// ============================================================

// ------------------------------------------------------------
// THE FORMULA
// ------------------------------------------------------------

// ------------------------------------------------------------
//  ⚑⚑ THE STANDING LYF LAW + THE SLIVER (POWER DOCTRINE) ⚑⚑
//
//  Terror resets Lyf to its printed value at the end of every
//  turn so that CHIP DAMAGE IS WORTHLESS — break through in one
//  strike or don't swing. That is the Lyf Cycle, and it is the
//  beating heart of Terror's combat.
//
//  Annihilation was built with Lyf as FLAT, STATIC reduction
//  that is never spent. Nothing depletes, so nothing needs
//  resetting. An attack that cannot break your Lyf cannot break
//  it on the hundredth swing either.
//
//  So Annihilation is not MISSING the Lyf Cycle. It lives
//  permanently INSIDE it. The law is the default state of the
//  world here, and that is why the no-compensation ruling on the
//  Lyf-0 Chaos Dragon (roadmap §4.33 / Doctrine 4c) is
//  structurally safe: there is no cycle it is being denied.
//  There is only the number.
//
//  THE SLIVER is the single clause that stops that law from
//  producing literal invincibility: no hit ever deals zero.
//
//  ⚑⚑ THE SLIVER IS NOT A DIAL. ⚑⚑
//  It reads like a tuning number sitting in a damage formula and
//  the next balance pass WILL reach for it. Don't. Raise it to 2
//  or scale it with level and attrition comes straight back: a
//  Lyf wall becomes a Lyf speed-bump, "break through or don't
//  swing" dies, and the Empire's third Law dies with it. One
//  damage against a four-hundred-Anima Lord is a rounding error,
//  which is exactly what worthless chip damage should feel like.
//  Every other number in this file is a dial. This one is
//  architecture. (roadmap §4.42)
// ------------------------------------------------------------
export const SLIVER = 1;

// ------------------------------------------------------------
//  ⚑⚑ THE BEAT (POWER DOCTRINE) ⚑⚑
//  Terror's cadence tags — OPT / OP2 / OP3 — are copied verbatim
//  into every IE title, and they measure TURNS. Annihilation has
//  no turns, so it needs a beat, and one constant defines the
//  cadence of every Tome in the game at once.
//
//  Out of combat the beat clock is STOPPED — you cannot idle in
//  the Plaza and tick your wards up.
//
//  DIAL: this single number retunes every cadence tag, globally.
// ------------------------------------------------------------
export const BEAT_SECONDS = 10;

export const CADENCE = {
  OPT: BEAT_SECONDS, //      once per beat
  OP2: BEAT_SECONDS * 2, //  once per two beats
  OP3: BEAT_SECONDS * 3, //  once per three beats
};

// THE RUIN LAW: no tag → consumed after use.
// THE FARE LAW: the activation cost is paid the FIRST time only.
// "You paid to light the fire; the fire keeps burning."
export const PERSISTENT_TAGS = ["OPT", "OP2", "OP3", "ATV"];
export const isPersistent = (tag) => PERSISTENT_TAGS.includes(tag);

// ------------------------------------------------------------
// DECREE — the earned opening.
//
// Internal id `decree` never moves (vyren pattern); the display
// string may retune. It sits in the same imperial register as
// Emperium and King's Grace, which is where it belongs.
//
// Every trigger is something the PLAYER did:
//   behind    — you got there
//   marked    — you set it up (staggered, stunned, Vessel-Marked)
//   comboEnd  — you didn't drop the string
//   primera   — you built for it (inside your own Primera window)
//
// ⚑⚑ POWER DOCTRINE — three more, all deterministic, all earned:
//   answered    — you struck within ANSWER_WINDOW_SEC of dodging
//                 that same enemy's attack. You read it.
//   vigil       — you are holding an unbroken Vigil (no Chao
//                 spent this engagement). RESTRAINT IS NOW A LIVE
//                 COMBAT BUFF, not a slow economy decision — and
//                 the player feels it break the frame they cast.
//   outnumbered — three or more enemies within OUTNUMBERED_RADIUS.
//                 You stood in it.
//
// ⚑ THE NO-LIKELIHOOD LAW. No node, item, Tome, Affinity or
// booster anywhere in Annihilation may increase the CHANCE of a
// Decree, because likelihood is not part of it. Nodes make
// Decrees BIGGER or add NEW TRIGGERS. That is the entire allowed
// vocabulary, forever. If a design doc says "crit chance," it is
// not an IE design yet.
//
// DIALS: every number below. Scale is the Doctrine's +1/+3 —
// a typical node is worth +1, a strong one +3.
// ------------------------------------------------------------
export const DECREE_TRIGGERS = {
  behind: "behind",
  marked: "marked",
  comboEnd: "combo_end",
  primera: "primera",
  answered: "answered",
  vigil: "vigil",
  outnumbered: "outnumbered",
};

export const DECREE_VALUES = {
  [DECREE_TRIGGERS.behind]: 3,
  [DECREE_TRIGGERS.marked]: 3,
  [DECREE_TRIGGERS.comboEnd]: 2,
  [DECREE_TRIGGERS.primera]: 2,
  [DECREE_TRIGGERS.answered]: 3,
  [DECREE_TRIGGERS.vigil]: 2,
  [DECREE_TRIGGERS.outnumbered]: 2,
};

// Windows the controller checks when emitting the new triggers.
// DIALS, both of them.
export const ANSWER_WINDOW_SEC = 0.5; // dodge → strike, same enemy
export const OUTNUMBERED_RADIUS = 5; // metres
export const OUTNUMBERED_COUNT = 3; // enemies inside that radius

// STACKING (roadmap §4.36, V'ael's call — reversible): triggers
// DO stack, but the second and third are worth less than the
// first, so setting up all four is a real flex without being
// mandatory. Implemented as flat integers rather than a falloff
// multiplier, because a falloff multiplier is a percentage
// wearing a hat: the highest Decree lands in full, and each
// ADDITIONAL trigger adds a flat +1.
export const DECREE_STACK_BONUS = 1;

/**
 * Flat Decree bonus for a set of triggers. Unknown trigger names
 * contribute nothing (so a zone can emit a trigger this build
 * doesn't know yet without inflating damage).
 * @param {string[]} triggers
 * @returns {number}
 */
export function resolveDecree(triggers = []) {
  const values = triggers
    .map((t) => DECREE_VALUES[t] || 0)
    .filter((v) => v > 0)
    .sort((a, b) => b - a);
  if (!values.length) return 0;
  return values[0] + (values.length - 1) * DECREE_STACK_BONUS;
}

/**
 * Resolve one instance of damage.
 *
 * Decree is added AFTER Lyf subtraction, on purpose: an earned
 * opening should land its full value even against a wall. That
 * is the whole difference between skill and a dice roll.
 *
 * @param {object} args
 * @param {number} args.attack       attacker's current Attack stat
 * @param {number} [args.skillBonus] the ability's flat bonus (additive, never multiplicative)
 * @param {number} args.targetLyf    target's Lyf (flat reduction)
 * @param {string[]} [args.decreeTriggers] earned openings this hit satisfied
 * @param {number} [args.decreeBonus] extra flat Decree from nodes/gear
 * @returns {{ damage: number, decree: boolean, decreeValue: number, crit: boolean }}
 */
export function resolveDamage({
  attack,
  skillBonus = 0,
  targetLyf,
  decreeTriggers = [],
  decreeBonus = 0,
}) {
  const decreeValue = resolveDecree(decreeTriggers) + Math.max(0, decreeBonus);
  // ⚑ THE SLIVER — see SLIVER below. Architecture, not a dial.
  const base = Math.max(SLIVER, attack + skillBonus - targetLyf);
  const damage = Math.round(base + decreeValue);
  const decree = decreeValue > 0;
  // `crit` is a COMPAT ALIAS, not a concept. Every zone's hit-FX
  // (whisperingGrove, sunkenFold, TrainingYard) flares gold on
  // `crit` — and a gold flare on an earned Decree is strictly
  // better than a gold flare on a dice roll, so the FX is right
  // and only the word is wrong. Delete this line when those four
  // files next get opened and switched to `decree`.
  return { damage, decree, decreeValue, crit: decree };
}

/**
 * DoT ticks bypass Lyf entirely (spec law). One entry point so
 * no future system "helpfully" re-applies mitigation to burns.
 */
export const resolveDotTick = (perSecond) => Math.max(1, Math.round(perSecond));

// ------------------------------------------------------------
// STATUS EFFECTS — flat damage over time or behavioral modifiers.
// Never percentage reductions. Spec table, verbatim.
// One registry; every applier and every UI icon reads from it.
// ------------------------------------------------------------
export const STATUS_EFFECTS = {
  burn: {
    id: "burn",
    name: "Burn",
    kind: "dot",
    dps: 5, // unmodified by Lyf
    durationSec: 5,
    refreshOnReapply: true,
    source: "Chaos Dragon Emperium, fire Tomes",
  },
  bleed: {
    id: "bleed",
    name: "Bleed",
    kind: "dot",
    dps: 3, // unmodified by Lyf
    durationSec: 5,
    refreshOnReapply: true,
    source: "Spectral Tiger U'hlti, elite bite attacks",
  },
  stun: {
    id: "stun",
    name: "Stun",
    kind: "control",
    durationSec: [1, 3], // per-source within this window
    effect: "Cannot act, cannot cast.",
    decreeTrigger: DECREE_TRIGGERS.marked, // a staggered target is an earned opening
    source: "Natural Disaster Primera, certain boss attacks",
  },
  dream: {
    id: "dream",
    name: "Dream",
    kind: "control",
    durationSec: [5, 8],
    effect: "Cannot act; takes double damage from Xulan.",
    source: "Xulan Emperium, Sweet Dream U'hlti",
  },
  vessel_mark: {
    id: "vessel_mark",
    name: "Vessel Mark",
    kind: "mark",
    durationSec: 8,
    effect: "Next Xulan ability deals +3 damage and always Decrees (consumed on proc).",
    // ⚑ GATE-1 (SMALL NUMBER LAW): was "+20% damage". A mark
    // is an earned opening, so it now feeds Decree directly —
    // the mark IS the setup. DIAL: decreeTrigger value lives in
    // DECREE_VALUES above.
    decreeTrigger: DECREE_TRIGGERS.marked,
    source: "Xulan Lyt proc",
  },
  nihil: {
    id: "nihil",
    name: "Nihil",
    kind: "corruption",
    durationSec: null, // until cleansed at a village
    stacking: true,
    effect: "Degrades ability effectiveness; stacks over time in the fog.",
    source: "Fog exposure, corruption enemies",
  },
};

// ------------------------------------------------------------
// LYT ECONOMY — each Lyt swing costs 1 from the small Lyt pool.
// Running dry is a ~2-second breather, not a punishment.
// ------------------------------------------------------------
export const LYT_SWING_COST = 1;

/**
 * @returns {{ ok: boolean, lyt: number }} whether the swing can
 * happen, and the pool after paying for it.
 */
export const payLytSwing = (currentLyt) =>
  currentLyt >= LYT_SWING_COST
    ? { ok: true, lyt: currentLyt - LYT_SWING_COST }
    : { ok: false, lyt: currentLyt };

// ------------------------------------------------------------
// REST GATING — the meta-rhythm. Rest-gated abilities refresh
// ONLY at a village (all of them) or partially at camps/bonfires
// in the field. Never on a timer in the wild. Fog radius blocks
// rest-gated recovery entirely (Systems Spec + Build Guide).
// ------------------------------------------------------------
export const REST_SCOPES = {
  VILLAGE: "village", // full reset of all rest-gated abilities
  FIELD_BONFIRE: "field_bonfire", // partial — some quick-rest abilities only
};

/**
 * Reset rest-gated cooldown flags on a character's ability state.
 * `abilities` is a map of { [abilityId]: { restGated, quickRest,
 * available, ... } } — returns a NEW map, untouched input.
 */
export function applyRest(abilities, scope) {
  const out = {};
  for (const [id, a] of Object.entries(abilities || {})) {
    if (!a?.restGated) {
      out[id] = a;
    } else if (scope === REST_SCOPES.VILLAGE) {
      out[id] = { ...a, available: true };
    } else if (scope === REST_SCOPES.FIELD_BONFIRE && a.quickRest) {
      out[id] = { ...a, available: true };
    } else {
      out[id] = a;
    }
  }
  return out;
}
